import type { BrokerProduct } from '@/services/brokers/types';
import { toaster } from '@/shared/ui/toaster.component';
import {
  Button,
  Checkbox,
  Dialog,
} from '@noorden-insurtech/alba-design-system';
import {
  ChevronDown,
  ChevronRight,
  EllipsisVertical,
  Folder,
  House,
  Package,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useUpdateBrokerProducts } from '../hooks/use-update-broker-products.hook';
import {
  groupProductsByGroup,
  type ProductGroup,
} from '../utils/product-groups.util';

type CheckboxState = boolean | 'indeterminate';

interface BrokerProductsModalProps {
  brokerId: string;
  isOpen: boolean;
  onClose: () => void;
  products: BrokerProduct[];
}

type SelectionMap = Record<string, boolean>;
type ExpansionMap = Record<string, boolean>;

function getItemKey(item: {
  id?: string;
  productId?: string;
  productCode?: string;
  name?: string;
}) {
  return item.id ?? item.productId ?? item.productCode ?? item.name;
}

export function BrokerProductsModal({
  brokerId,
  isOpen,
  onClose,
  products,
}: BrokerProductsModalProps) {
  const groupedByCategory = useMemo(
    () => groupProductsByGroup(products),
    [products]
  );
  const {
    error: mutationError,
    isPending: isSaving,
    reset: resetUpdate,
    mutateAsync: updateProducts,
  } = useUpdateBrokerProducts(brokerId);
  const mutationErrorMessage = mutationError?.message ?? null;

  const [selected, setSelected] = useState<SelectionMap>({});
  const [expanded, setExpanded] = useState<ExpansionMap>({});

  useEffect(() => {
    if (!isOpen || !products.length) return;

    setSelected(() => {
      const next: SelectionMap = {};
      products.forEach(product => {
        const productKey = getItemKey(product);
        if (!productKey) return;
        const isActive = String(product.status).toLowerCase() === 'active';
        next[productKey] = isActive;
      });
      return next;
    });

    setExpanded({});
  }, [isOpen, products]);

  useEffect(() => {
    if (isOpen) return;
    resetUpdate();
  }, [isOpen, resetUpdate]);

  const flatIds = useMemo(() => {
    const keys: string[] = [];
    products.forEach(product => {
      const productKey = getItemKey(product);
      if (!productKey) return;
      keys.push(productKey);
    });
    return keys;
  }, [products]);

  const allSelected = flatIds.length > 0 && flatIds.every(id => selected[id]);

  const toggleAll = () => {
    const nextValue = !allSelected;
    const next: SelectionMap = {};
    flatIds.forEach(id => {
      next[id] = nextValue;
    });
    setSelected(next);
  };

  const toggleExpand = (category: string) => {
    setExpanded(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const resolveCategoryState = (group: ProductGroup): CheckboxState => {
    const keys = group.products
      .map(p => getItemKey(p))
      .filter((k): k is string => Boolean(k));
    if (!keys.length) return false;

    const allChecked = keys.every(k => selected[k]);
    const someChecked = keys.some(k => selected[k]);

    if (allChecked) return true;
    if (someChecked) return 'indeterminate';
    return false;
  };

  const handleCategoryToggle = (group: ProductGroup) => {
    const current = resolveCategoryState(group) === true;
    const nextValue = !current;
    setSelected(prev => {
      const next = { ...prev };
      group.products.forEach(product => {
        const key = getItemKey(product);
        if (!key) return;
        next[key] = nextValue;
      });
      return next;
    });
  };

  const handleProductToggle = (product: BrokerProduct) => {
    const key = getItemKey(product);
    if (!key) return;
    setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (!brokerId) return;

    const payload = products.flatMap(product => {
      const productKey = getItemKey(product);
      if (!productKey) return [];

      const wasActive = product.status === 'active';
      const isNowSelected = Boolean(selected[productKey]);

      if (wasActive === isNowSelected) return [];

      const nextStatus: 'ACTIVE' | 'INACTIVE' = isNowSelected
        ? 'ACTIVE'
        : 'INACTIVE';

      return [
        {
          id: product.id,
          category: product.category,
          partyId: product.partyId,
          productCode: product.productCode,
          productId: product.productId,
          productTypeId: product.productTypeId,
          productDescription: product.description,
          productName: product.name,
          status: nextStatus,
        },
      ];
    });

    if (!payload.length) {
      onClose();
      return;
    }

    try {
      await updateProducts(payload);
      setSelected(prev => {
        const next = { ...prev };
        payload.forEach(item => {
          const key =
            item.id ?? item.productId ?? item.productCode ?? undefined;
          if (key) {
            next[key] = item.status === 'ACTIVE';
          }
        });
        return next;
      });

      onClose();
    } catch (error) {
      // O hook desfaz o update otimista; aqui só avisamos o usuário. A mensagem
      // é o `detail` do ProblemDetails da Api (ex.: corretora inválida ou sem
      // status no InsureMO bloqueia a ativação).
      toaster.create({
        type: 'error',
        title: 'Erro ao salvar produtos',
        description:
          error instanceof Error && error.message.trim()
            ? error.message.trim()
            : 'Falha ao atualizar produtos',
      });
    }
  };

  const renderCaret = (isExpanded: boolean, disabled: boolean) =>
    disabled ? (
      <div className='w-4 h-6' />
    ) : isExpanded ? (
      <ChevronDown className='h-4 w-4 text-alba-gray-500' strokeWidth={1.6} />
    ) : (
      <ChevronRight className='h-4 w-4 text-alba-gray-500' strokeWidth={1.6} />
    );

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[680px] max-w-[90vw] rounded-3xl bg-[#F9F9F9] shadow-xl p-0'>
          <Dialog.Header className='px-6 pt-6 pb-0'>
            <Dialog.Title className='w-full'>
              <div className='flex items-center gap-3 text-alba-black-500'>
                <Package className='h-6 w-6' strokeWidth={1.8} />
                <span className='text-xl font-bold leading-6'>
                  Gerenciar Produtos
                </span>
              </div>
            </Dialog.Title>
          </Dialog.Header>

          <Dialog.Body className='px-6 pb-6 pt-4'>
            <div className='flex flex-col gap-8'>
              <div className='flex flex-col gap-4'>
                {mutationErrorMessage && (
                  <div className='text-sm text-alba-error-500'>
                    {mutationErrorMessage}
                  </div>
                )}
                <TreeItem
                  isRoot
                  label='Selecionar todos'
                  state={allSelected}
                  onToggle={toggleAll}
                />

                <div className='flex flex-col gap-2 pl-2'>
                  {groupedByCategory.map(group => {
                    const categoryKey = group.key || 'DEFAULT';
                    const hasChildren = group.products.length > 0;
                    const isExpanded = expanded[categoryKey] ?? false;
                    const state = resolveCategoryState(group);

                    return (
                      <div
                        key={categoryKey}
                        className='flex flex-col rounded-xl px-1 transition-colors'
                      >
                        <div className='flex items-center gap-2 py-1'>
                          <button
                            type='button'
                            onClick={() => toggleExpand(categoryKey)}
                            disabled={!hasChildren}
                            className={`flex h-6 w-6 items-center justify-center rounded-md ${
                              hasChildren
                                ? 'text-alba-gray-500 hover:bg-alba-gray-200'
                                : 'opacity-40'
                            }`}
                          >
                            {renderCaret(isExpanded, !hasChildren)}
                          </button>

                          <Checkbox.Root
                            checked={state}
                            onCheckedChange={() => handleCategoryToggle(group)}
                            onClick={() => handleCategoryToggle(group)}
                          >
                            <Checkbox.Control className='h-5 w-5'>
                              <Checkbox.Indicator />
                            </Checkbox.Control>
                          </Checkbox.Root>

                          <FolderIcon />
                          <span className='text-base text-alba-gray-700'>
                            {group.label}
                          </span>

                          <div className='ml-auto pr-2 text-alba-gray-400'>
                            <button
                              type='button'
                              className='flex h-7 w-7 items-center justify-center rounded-md'
                              aria-label='Ações da categoria'
                              disabled
                            >
                              <EllipsisVertical
                                className='h-5 w-5'
                                strokeWidth={1.6}
                                style={{ color: '#B7BBBC' }}
                              />
                            </button>
                          </div>
                        </div>

                        {hasChildren && isExpanded && (
                          <div className='ml-8 flex flex-col gap-0.5 border-l border-alba-gray-200 pl-4'>
                            {group.products.map(product => {
                              const productKey =
                                getItemKey(product) ?? product.productCode;
                              const checked = Boolean(
                                productKey && selected[productKey]
                              );

                              return (
                                <div
                                  key={productKey}
                                  className='flex items-center gap-2 py-0.5'
                                >
                                  <div className='w-6' />
                                  <Checkbox.Root
                                    checked={checked}
                                    onCheckedChange={() =>
                                      handleProductToggle(product)
                                    }
                                    onClick={() => handleProductToggle(product)}
                                  >
                                    <Checkbox.Control className='h-5 w-5'>
                                      <Checkbox.Indicator />
                                    </Checkbox.Control>
                                  </Checkbox.Root>

                                  <SubProductIcon />
                                  <span className='text-base text-alba-gray-700'>
                                    {product.name}
                                  </span>

                                  <div className='ml-auto pr-2 text-alba-gray-400'>
                                    <button
                                      type='button'
                                      className='flex h-7 w-7 items-center justify-center rounded-md disabled:opacity-50'
                                      disabled
                                      aria-label='Ações do produto'
                                    >
                                      <EllipsisVertical
                                        className='h-5 w-5'
                                        strokeWidth={1.6}
                                        style={{ color: '#B7BBBC' }}
                                      />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Dialog.Body>

          <Dialog.Footer className='flex justify-end gap-4 px-6 pb-6 pt-0'>
            <Button variant='ghost' onClick={onClose}>
              Cancelar
            </Button>
            <Button variant='primary' onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

interface TreeItemProps {
  label: string;
  state: CheckboxState;
  onToggle: () => void;
  icon?: ReactNode;
  isRoot?: boolean;
}

function TreeItem({
  label,
  state,
  onToggle,
  icon,
  isRoot = false,
}: TreeItemProps) {
  return (
    <div className='flex items-center gap-3 rounded-lg px-1'>
      <Checkbox.Root
        checked={state}
        onCheckedChange={onToggle}
        onClick={onToggle}
      >
        <Checkbox.Control className='h-5 w-5'>
          <Checkbox.Indicator />
        </Checkbox.Control>
      </Checkbox.Root>
      {icon}
      <span
        className={`text-base ${
          isRoot ? 'text-alba-gray-500' : 'text-alba-gray-700'
        }`}
      >
        {label}
      </span>
    </div>
  );
}

function FolderIcon() {
  return <Folder className='h-5 w-5 text-alba-gray-500' strokeWidth={1.6} />;
}

function SubProductIcon() {
  return <House className='h-4 w-4 text-alba-gray-500' strokeWidth={1.6} />;
}
